import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { closeSnackbar, useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  ItemsProfileService,
  RackProfileService,
} from "subModule/src/core/services";
import {
  SaveStockReceiptResponse,
  StockReceiptResponse,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { DeleteData } from "subModule/src/type";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import DynamicSelect from "common/Select/Select";
import {
  useGetPurchaseOrdersTypeService,
  useGetStockReceivedApprovedService,
} from "subModule/src/services/inventory/useInventory";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { normalizeBinsList } from "subModule/src/services/rack/service";
import { isBinInventoryMode } from "pages/Store/Racks/rackUtils";

interface FormData {
  [key: string]: any;
}

const StockReceivedCreateUpdate: React.FC = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "Stock Received",
    moduleHref: "/inventory/stock-receives",
    entityFallbackLabelKey: "Stock Received",
    isEdit: !!params?.id,
    action: "details",
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    getValues,
    reset,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      stockReceiptItems: [],
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: watch("storeId")?.value,
      search: dropdownSearchTerm,
    },
  );
  const { data: vendorsData, refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: dropdownPage,
    size: 50,
    title: dropdownSearchTerm,
  });
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: poData, refetch: poRefetch } = useGetPurchaseOrdersTypeService({
    pageNumber: dropdownPage,
    size: 50,
    type: params?.id ? "" : 2,
    poNumber: dropdownSearchTerm,
  });
  const { data: invoiceData, refetch: invoicesRefetch } =
    useGetStockReceivedApprovedService({
      pageNumber: dropdownPage,
      size: 20,
      type: 2,
      invoiceNumber: dropdownSearchTerm,
    });
  const [stockReceiptItems, setStockReceiptItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [sr, setSR] = useState<StockReceiptResponse>();
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const [partsLoader, setPartsLoader] = useState<boolean>(false);
  const [storeBins, setStoreBins] = useState<
    { value: number; label: string }[]
  >([]);
  const selectedStore = watch("storeId");
  const isStoreBinMode = isBinInventoryMode(selectedStore?.inventoryMode);
  useEffect(() => {
    vendorsRefetch();
    storesRefetch();
    poRefetch();
    invoicesRefetch();
  }, []);
  useEffect(() => {
    if (params?.id && poData) {
      getStockReceiptById();
    }
  }, [params, poData]);
  const getParts = () => {
    itemsRefetch()
      .then((res: GetItemsDropdownResponse | unknown | any) => {
        if (res?.data?.pages[0]?.success) {
          ///
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => setPartsLoader(false));
  };
  const getStockReceiptById = () => {
    InventoryProfileService.getEntityById(params?.id, "stockreceipt")
      .then((res: any) => {
        if (res.success) {
          setSR(res.data as any);
          captureLabel(
            (res.data as any)?.stockNumber != null
              ? String((res.data as any).stockNumber)
              : null,
          );
          setStockReceiptItems((res.data as any)?.stockReceiptItems || []);
          setValue(
            "stockReceiptItems",
            (res.data as any)?.stockReceiptItems || [],
          );
          setValue("supplierId", {
            value: (res.data as any)?.supplierId,
            label: (res.data as any)?.vendorTitle,
          });
          setValue(
            "storeId",
            resolveStoreOption(
              (res.data as any)?.storeId,
              (res.data as any)?.storeTitle,
            ),
          );
          setValue("type", "stock");
          if (res?.data?.poId)
            setValue("poId", {
              value: res?.data?.poId,
              label:
                poOptions?.find((option) => option.value === res?.data?.poId)
                  ?.label || res?.data?.poId,
            });
          if (res?.data?.purchaseInvoiceId)
            setValue("purchaseInvoiceId", {
              value: res?.data?.purchaseInvoiceId,
              label: res?.data?.purchaseInvoiceNo,
            });
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  // const getPOById = (id: string | undefined) => {
  //   InventoryProfileService.getPurchaseOrderById(id)
  //     .then((res: any) => {
  //       if (res.success) {
  //         setSR(res.data);
  //         setStockReceiptItems(res.data?.purchaseOrderItems);
  //         setValue("stockReceiptItems", res.data.purchaseOrderItems);
  //         setValue("storeId", {
  //           value: res.data?.storeId,
  //           label: res.data?.storeTitle,
  //         });
  //         setValue("supplierId", {
  //           value: res.data?.supplierId,
  //           label: res.data?.vendorTitle,
  //         });
  //         setValue("type", "stock");
  //         setLoading(false);
  //       } else {
  //         enqueueSnackbar(
  //           <AlertMessages text={res.message || "Something went wrong"} />,
  //           { variant: "error" }
  //         );
  //         setLoading(false);
  //       }
  //     })
  //     .catch((err: any) => {
  //       console.error("===", err);
  //       setLoading(false);
  //     });
  // };
  const getInvoiceById = (id: string | undefined) => {
    InventoryProfileService.getInvoiceById(id, 2)
      .then((res: any) => {
        if (res.success) {
          setSR(res.data);
          setStockReceiptItems(res.data?.purchaseInvoiceItem);
          setValue("stockReceiptItems", res.data.purchaseInvoiceItem);
          setValue(
            "storeId",
            resolveStoreOption(res.data?.storeId, res.data?.storeTitle),
          );
          setValue("supplierId", {
            value: res.data?.supplierId,
            label: res.data?.vendorTitle,
          });
          setValue("type", "stock");
          setValue("poId", res.data?.poId);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string; inventoryMode?: number | null }) => ({
      value: item.id,
      label: item.title,
      inventoryMode: item.inventoryMode ?? null,
    }),
  );
  const resolveStoreOption = (storeId: number, storeTitle: string) => {
    const match = storeOptions.find((s) => s.value === storeId);
    return {
      value: storeId,
      label: storeTitle,
      inventoryMode: match?.inventoryMode ?? null,
    };
  };
  const loadStoreBins = (storeId: number) => {
    if (!storeId) {
      setStoreBins([]);
      return Promise.resolve([]);
    }
    setSearchLoading("bins");
    return RackProfileService.getBinsByStore(storeId)
      .then((res: any) => {
        const list = normalizeBinsList(res).map((bin) => ({
          value: bin.id,
          label: bin.locationCode || String(bin.id),
        }));
        setStoreBins(list);
        return list;
      })
      .catch((err: any) => {
        console.error("===", err);
        setStoreBins([]);
        return [];
      })
      .finally(() => setSearchLoading(null));
  };
  const findBinByLocationCode = (
    locationCode: string | undefined | null,
    bins: { value: number; label: string }[],
  ) => {
    if (!locationCode) return null;
    const code = String(locationCode).trim();
    return (
      bins.find((b) => b.label === code) ||
      bins.find((b) => b.label.endsWith(`-${code}`) || b.label === code) ||
      null
    );
  };
  const resolveStoreBinOption = (
    locationRes: any,
    bins: { value: number; label: string }[],
    fallbackLocationCode?: string,
  ) => {
    const data = locationRes?.data;
    if (data) {
      const binId = Number(data.binId ?? data.id) || null;
      const locationCode = data.locationCode || data.code || "";
      if (binId) {
        return (
          bins.find((b) => b.value === binId) || {
            value: binId,
            label: locationCode || String(binId),
          }
        );
      }
      const matched = findBinByLocationCode(locationCode, bins);
      if (matched) return matched;
    }
    return findBinByLocationCode(fallbackLocationCode, bins);
  };
  const applyBinSelection = (
    index: number,
    option: { value: number; label: string } | null,
  ) => {
    setStockReceiptItems((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], destinationBinId: option };
      return next;
    });
    setValue(`stockReceiptItems.${index}.destinationBinId`, option);
  };
  const fetchItemStoreLocation = async (
    index: number,
    itemId: number,
    storeId: number,
    fallbackLocationCode?: string,
    bins?: { value: number; label: string }[],
  ) => {
    if (!itemId || !storeId) return;
    let binsList = bins || storeBins;
    if (!binsList.length) {
      binsList = await loadStoreBins(storeId);
    }
    // Prefer matching from item dropdown locationCode immediately.
    const immediate = findBinByLocationCode(fallbackLocationCode, binsList);
    if (immediate) applyBinSelection(index, immediate);
    try {
      const res = await ItemsProfileService.getItemLocation(storeId, itemId);
      const option = resolveStoreBinOption(res, binsList, fallbackLocationCode);
      applyBinSelection(index, option);
    } catch (err) {
      console.error("===", err);
      if (!immediate) applyBinSelection(index, null);
    }
  };
  useEffect(() => {
    if (selectedStore?.value) {
      setPartsLoader(true);
      getParts();
      if (isBinInventoryMode(selectedStore?.inventoryMode)) {
        loadStoreBins(selectedStore.value);
      } else {
        setStoreBins([]);
      }
    }
  }, [selectedStore?.value, selectedStore?.inventoryMode]);
  const prepareStockReceiptItemsPayload = (items: any[]) =>
    items.map((row: any) => {
      const binId =
        row?.destinationBinId?.value ?? row?.destinationBinId ?? null;
      const payload = { ...row };
      delete payload.unitTitle;
      delete payload.id;
      delete payload.destinationBinId;
      payload.receivedDate =
        payload.receivedDate?.split(" ")[0] ||
        payload.receivedDate ||
        dayjs(new Date()).format("YYYY-MM-DD");
      if (isStoreBinMode && binId) {
        payload.binId = Number(binId);
      }
      return payload;
    });
  const poOptions = (poData?.pages[0]?.data?.content || [])?.map(
    (item: any) => ({ value: item.id, label: item.poNumber }),
  );
  const invoices = (invoiceData?.pages[0]?.data?.content || [])?.map(
    (item: any) => ({ value: item.id, label: item.invoiceNo }),
  );
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "batchNumber",
      label: t("Batch"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "receivedDate",
      label: t("Received") + " " + t("Date"),
      numeric: false,
      disablePadding: false,
      type: "date",
    },
    {
      id: "expiryDate",
      label: t("Expiry") + " " + t("Date"),
      numeric: false,
      disablePadding: false,
      type: "date",
    },
    ...(!params?.id ? [{ id: "locationCode", label: t("location") }] : []),
    ...(!params?.id && isStoreBinMode
      ? [
          {
            id: "destinationBinId",
            label: t("BIN"),
            numeric: false,
            type: "select",
          },
        ]
      : []),
  ];
  const DeleteItem = (row: any, typeName: string) => {
    if (typeName === "stockReceiptItems" && params?.id) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "stockreceipt/items",
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getStockReceiptById();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    } else {
      setStockReceiptItems((prevRows) => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue("stockReceiptItems", []);
        setTimeout(
          () =>
            setValue("stockReceiptItems", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        );
        return newRows;
      });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "stockReceiptItems") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      if (!params?.id) {
        (newItem as any).id = Date.now();
        (newItem as any).receivedDate = dayjs(new Date()).format("YYYY-MM-DD");
      }
      setStockReceiptItems((prevRows) => [...prevRows, newItem]);
    }
  };
  const handleItem = (row: any, typeName: string, action: string) => {
    if (typeName === "stockReceiptItems" && params?.id) {
      if (action === "create") {
        InventoryProfileService.createStockReceiptItem({
          ...row,
          stockReceiptId: sr?.id,
          storeId: sr?.storeId,
          receivedDate:
            row.receivedDate?.split(" ")[0] ||
            row.receivedDate ||
            dayjs(new Date())?.format("YYYY-MM-DD"),
          expiryDate: row.expiryDate || null,
        })
          .then((response: SaveStockReceiptResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={t("created") + " " + t("successfully")}
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getStockReceiptById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getStockReceiptById();
          });
      } else {
        InventoryProfileService.updateStockReceiptItem({
          ...row,
          stockReceiptId: sr?.id,
          storeId: sr?.storeId,
          receivedDate: row.receivedDate?.split(" ")[0] || row.receivedDate,
          expiryDate: row.expiryDate?.split(" ")[0] || row.expiryDate || null,
        })
          .then((response: SaveStockReceiptResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={t("updated") + " " + t("successfully")}
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getStockReceiptById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getStockReceiptById();
          });
      }
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "stockReceiptItems") {
      const updatedItems = [...stockReceiptItems];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setStockReceiptItems(updatedItems);
    }
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length || Object.values(getValues())?.length) {
      setIsBtnEnabled(true);
      if (!watch("supplierId")?.value || !watch("storeId")?.value) {
        setIsBtnEnabled(false);
        return;
      }
      const filteredItems = stockReceiptItems.filter(
        (item: any) => item?.itemName,
      );
      if (filteredItems.length === 0) {
        closeSnackbar();
        enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
        return;
      }
      const payloadItems = prepareStockReceiptItemsPayload(filteredItems);
      if (!watch("poId")?.value && !watch("purchaseInvoiceId")?.value) {
        InventoryProfileService.saveStockReceiptWithInvoice(
          Object.values(data)?.length
            ? {
                ...data,
                storeId: data.storeId.value,
                supplierId: data.supplierId.value,
                stockReceiptItems: payloadItems,
                poId: watch("poId")?.value || watch("poId") || null,
                purchaseInvoiceId: watch("purchaseInvoiceId")?.value || null,
              }
            : {
                ...getValues(),
                storeId: watch("storeId").value,
                supplierId: watch("supplierId").value,
                stockReceiptItems: payloadItems,
                poId: watch("poId")?.value || watch("poId") || null,
                purchaseInvoiceId: watch("purchaseInvoiceId")?.value || null,
              },
        )
          .then((response: SaveStockReceiptResponse) => {
            if (response.success) {
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Stock Received") +
                    " " +
                    t("saved") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
              // LinkStockReceipt(response.data);
              navigate(`/inventory/stock-receives/${response?.data?.id}/print`);
            } else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            setLoading(false);
          })
          .catch((err: any) => {
            console.error("===", err);
            setLoading(false);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
          })
          .finally(() => setIsBtnEnabled(false));
      } else {
        InventoryProfileService.saveStockReceipt(
          Object.values(data)?.length
            ? {
                ...data,
                storeId: data.storeId.value,
                supplierId: data.supplierId.value,
                stockReceiptItems: payloadItems,
                poId: watch("poId")?.value || watch("poId") || null,
                purchaseInvoiceId: watch("purchaseInvoiceId")?.value || null,
              }
            : {
                ...getValues(),
                storeId: watch("storeId").value,
                supplierId: watch("supplierId").value,
                stockReceiptItems: payloadItems,
                poId: watch("poId")?.value || watch("poId") || null,
                purchaseInvoiceId: watch("purchaseInvoiceId")?.value || null,
              },
        )
          .then((response: SaveStockReceiptResponse) => {
            if (response.success) {
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Stock Received") +
                    " " +
                    t("saved") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
              // if(watch("purchaseInvoiceId")?.value) LinkStockReceipt(response.data);
              // else
              navigate(`/inventory/stock-receives/${response?.data?.id}`);
            } else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            setLoading(false);
          })
          .catch((err: any) => {
            console.error("===", err);
            setLoading(false);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
          })
          .finally(() => setIsBtnEnabled(false));
      }
    }
  };
  const LinkStockReceipt = (data?: any) => {
    setIsBtnEnabled(true);
    if (!watch("purchaseInvoiceId")?.value && !data) {
      trigger("purchaseInvoiceId");
      setIsBtnEnabled(false);
      return;
    }
    InventoryProfileService.linkStockReceiptToInvoice({
      stockId: +(params as any)?.id || data?.id,
      ...((watch("poId")?.value || data?.poId) && {
        poId: watch("poId")?.value || data?.poId,
      }),
      ...((watch("purchaseInvoiceId")?.value || data?.purchaseInvoiceId) && {
        invoiceId: watch("purchaseInvoiceId")?.value || data?.purchaseInvoiceId,
      }),
    })
      .then((res: any) => {
        if (res.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={res.status}
              text={
                t("Stock Received") +
                " " +
                t("authorized") +
                " " +
                t("successfully")
              }
            />,
            { variant: "success" },
          );
          navigate(`/inventory/stock-receives/${res?.data?.id}/print`);
        } else {
          enqueueSnackbar(<AlertMessages text={res?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={t(
              err?.message || err?.data?.message || "Something went wrong",
            )}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setIsBtnEnabled(false));
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stores")
        storesRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "purchaseOrders")
        poRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "purchaseInvoices")
        invoicesRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/stock-receives")}
        onSubmit={() => {}}
        heading={t("Stock Received")}
        icon={
          <InventoryIcon sx={{ fill: "#000", width: "28px", height: "28px" }} />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      <Grid
        container
        component="form"
        id="form"
        className="px-[8px] items-baseline"
        rowGap={2.5}
        columnGap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container className="items-center">
          <Grid item xs={12} sm={6} className="text-start">
            <Typography
              className="font-Saira text-[15px] font-semibold"
              style={{ color: themeColors?.primary }}
            >
              {params?.id ? t("Stock Received") : t("New Stock")} {t("Details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} className="hide-on-print">
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || partsLoader}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("Save as received")}
                </Button>
              )}
              {params?.id ? (
                <>
                  <Button
                    className="primary"
                    disabled={IsBtnEnabled || partsLoader}
                    style={{ background: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() => LinkStockReceipt()}
                  >
                    {t("Authorize")}
                  </Button>
                  {/* <Button
                  className="secondary"
                  style={{ color: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => navigate(`${window.location.pathname}/print`)}
                >
                  {t("Print")}
                </Button> */}
                </>
              ) : null}
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => navigate("/inventory/stock-receives")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>

        {params?.id ? (
          <Grid
            container
            className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
            style={{ background: themeColors?.primary }}
          >
            <Grid item xs={4}>
              <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                {t("Stock Received")}
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Stock")} # </span>{" "}
                <span>{sr?.stockNumber || ""}</span>
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Date")}</span>{" "}
                <span>{sr?.receivedDate?.split("T")[0] || ""}</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="flex justify-end gap-10">
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("Supplier")} {t("name")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">{sr?.vendorTitle || ""}</span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("store") + " " + t("name")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">{sr?.storeTitle || ""}</span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Requested")}</span> <br />
                  <span className="font-medium">{sr?.totalQuantity || 0}</span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Received")}</span> <br />
                  <span className="font-medium">
                    {sr?.totalReceivedQuantity || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Amount")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      sr?.totalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
              </div>
            </Grid>
          </Grid>
        ) : null}

        <Grid
          container
          item
          xs={12}
          gap={2.5}
          className="stockTransferDetails flex-nowrap"
        >
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="purchaseInvoiceId"
              control={control}
              defaultValue={null}
              rules={{
                required: params?.id ? t("This field is required.") : false,
              }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("supplierBills") + (params?.id ? " *" : "")}
                  defaultPlaceholder="phSelectSupplierBill"
                  {...field}
                  options={invoices}
                  isSearchable
                  isClearable
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    if (!params?.id) {
                      reset(
                        { purchaseInvoiceId: selectedOption },
                        { keepValues: true },
                      );
                      setStockReceiptItems([]);
                      if (selectedOption?.value) {
                        setValue("poId", null);
                        setTimeout(
                          () => getInvoiceById(selectedOption.value),
                          500,
                        );
                      }
                      if (!selectedOption?.value) reset();
                    }
                  }}
                  error={errors.purchaseInvoiceId?.message as string}
                  isDisabled={
                    params?.id && !sr?.poId && sr?.purchaseInvoiceId
                      ? true
                      : false
                  }
                  isLoading={searchLoading === "purchaseInvoices"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions(
                        "purchaseInvoices",
                        "searchIt",
                        search,
                      );
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions(
                          "purchaseInvoices",
                          "searchIt",
                          "",
                        );
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(invoiceData?.pages[0]?.data as any).last)
                      handleDropdownActions("purchaseInvoices", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="supplierId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("vendors")}
                  defaultPlaceholder="phSelectSupplier"
                  {...field}
                  options={supplierOptions}
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                  }}
                  error={errors.supplierId?.message as string}
                  isDisabled={
                    (params?.id as any) || watch("purchaseInvoiceId")?.value
                  }
                  isLoading={searchLoading === "suppliers"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("suppliers", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("suppliers", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(vendorsData?.pages[0]?.data as any).last)
                      handleDropdownActions("suppliers", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="storeId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("stores")}
                  defaultPlaceholder="phSelectWarehouse"
                  {...field}
                  options={storeOptions}
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    if (!params?.id) {
                      setStockReceiptItems([]);
                      setValue("stockReceiptItems", []);
                    }
                    if (
                      !selectedOption?.value ||
                      !isBinInventoryMode(selectedOption?.inventoryMode)
                    ) {
                      setStoreBins([]);
                    }
                  }}
                  error={errors.storeId?.message as string}
                  isDisabled={
                    (params?.id as any) || watch("purchaseInvoiceId")?.value
                  }
                  isLoading={searchLoading === "stores"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("stores", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("stores", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(storesData?.pages[0]?.data as any).last)
                      handleDropdownActions("stores", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} className="relative stockTransferDetails">
          {loading || partsLoader ? (
            <Loader />
          ) : (
            <Box className="bg-white py-2 px-[18px] rounded-[8px]">
              <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                {t("Stock Received")}
              </Typography>
              <InventoryItemsTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columns}
                handleInputChange={handleInputChange}
                rows={stockReceiptItems}
                typeName="stockReceiptItems"
                items={itemsData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{
                  params,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                checkStatus={
                  params?.id ||
                  (!params?.id && watch("purchaseInvoiceId")?.value)
                    ? 2
                    : 1
                }
                viewDelete={true}
                hideDelete={params?.id ? true : false}
                enableQuantity={true}
                itemsResponse={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
                binOptions={storeBins}
                onItemSelected={(index, selectedOption) => {
                  if (
                    !isStoreBinMode ||
                    !selectedStore?.value ||
                    !selectedOption?.value
                  ) {
                    setValue(
                      `stockReceiptItems.${index}.destinationBinId`,
                      null,
                    );
                    return;
                  }
                  fetchItemStoreLocation(
                    index,
                    Number(selectedOption.value),
                    Number(selectedStore.value),
                    selectedOption.locationCode || "",
                  );
                }}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  {!params?.id &&
                  (watch("poId")?.value ||
                    watch("purchaseInvoiceId")?.value) ? null : (
                    <Button
                      className="secondary flex gap-1"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => addNewItem("stockReceiptItems")}
                    >
                      <AddIcon className="w-[20px] h-[20px]" />{" "}
                      {t("Add More Items")}
                    </Button>
                  )}
                </div>
              </Grid>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} className="hide-on-print">
          <Divider className="bg-[#AEB3C4] mt-[8px]" />
        </Grid>
        <Grid container className="items-center hide-on-print">
          <Grid item xs={12} sm={6} className="text-start"></Grid>
          <Grid item xs={12} sm={6}>
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || partsLoader || loading}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("Save as received")}
                </Button>
              )}
              {params?.id ? (
                <Button
                  className="primary"
                  disabled={IsBtnEnabled || partsLoader || loading}
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => LinkStockReceipt()}
                >
                  {t("Authorize")}
                </Button>
              ) : null}
              <Button
                className="secondary"
                variant="contained"
                style={{ color: themeColors?.primary }}
                disableElevation
                onClick={() => navigate("/inventory/stock-receives")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default StockReceivedCreateUpdate;
